import { httpClient } from '../httpClient/httpClient';
import { ProductApprovalState } from './productApprovalsApi';

export interface ProductGroupLeadTime {
  productGroupName: string;
  branches: BranchLeadTime[];
}

export interface BranchLeadTime {
  branchName: string;
  branchIdentifier: string;
  branchId: number;
  averageDays: number;
  count: number;
  productApprovalIds: number[];
}

export interface LeadTimeStatistics {
  productGroups: ProductGroupLeadTime[];
}

export interface LeadTimeStatisticsQuery {
  sampleSize?: number;
  customerId?: number;
  branchIds?: number[];
}
export type RatioStatisticsQuery = LeadTimeStatisticsQuery;
export type ArticleProgressStatisticsQuery = LeadTimeStatisticsQuery;

export interface ProductGroupRatio {
  productGroupName: string;
  branches: BranchRatio[];
}

export interface BranchRatio {
  branchName: string;
  branchIdentifier: string;
  branchId: number;
  averageRatio: number;
  compositionGroupCount: number;
}

export interface RatioStatistics {
  productGroups: ProductGroupRatio[];
}

export interface StageStatistics {
  stageName: string;
  count: number;
  percentage: number;
  articleIds: number[];
  missingArticleIds: number[];
}

export interface BranchStageStatistics {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  stages: StageData[];
}

export interface StageData {
  stageName: string;
  count: number;
  percentage: number;
}

export interface ArticleProgressStatistics {
  stages: StageStatistics[];
  earliestCreatedDate?: string;
  branchStages: BranchStageStatistics[];
}

export interface DayDistribution {
  dayCount: number;
  branches: BranchDistribution[];
  totalCount: number;
}

export interface BranchDistribution {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  count: number;
  productApprovalIds: number[];
}

export interface PaLeadTimeDistributionStatistics {
  dayDistributions: DayDistribution[];
}

export interface PaLeadTimeDistributionQuery {
  customerIds?: number[];
  branchIds?: number[];
  state?: ProductApprovalState;
  sampleSize?: number;
  supplierId?: number;
}

export interface SupplierCoverageHeatmapStatistics {
  branches: BranchHeatmapRow[];
  productGroups: ProductGroupColumn[];
}

export interface BranchHeatmapRow {
  branchId: number;
  branchName: string;
  branchIdentifier: string;
  cells: HeatmapCellData[];
}

export interface ProductGroupColumn {
  productGroupId: number;
  productGroupCode: string;
  productGroupValue: string;
}

export interface HeatmapCellData {
  productGroupId: number;
  supplierCount?: number;
  supplierIds?: number[];
  articleCount?: number;
  articleIds?: number[];
}

export interface SupplierCoverageHeatmapQuery {
  materialId?: number;
}

export interface ArticleCoverageHeatmapQuery {
  materialId?: number;
  createdAfter?: string;
}

export interface ArticleCoverageHeatmapStatistics {
  branches: BranchHeatmapRow[];
  productGroups: ProductGroupColumn[];
}

export interface RejectionReasonStat {
  reasonName: string;
  count: number;
  percentage: number;
  productApprovalIds: number[];
}

export interface PaRejectionReasonsStatistics {
  reasons: RejectionReasonStat[];
}

export interface PaRejectionReasonsQuery {
  sampleSize?: number;
  customerId?: number;
  branchIds?: number[];
  supplierId?: number;
}

export const statisticsApi = {
  getProductApprovalLeadTime: (params: LeadTimeStatisticsQuery) =>
    httpClient.get<LeadTimeStatistics>(
      `statistics/product-approval/lead-time`,
      {
        params,
      },
    ),
  getProductApprovalRatio: (params: RatioStatisticsQuery) =>
    httpClient.get<RatioStatistics>(`statistics/product-approval/ratio`, {
      params,
    }),
  getArticleProgress: (params: ArticleProgressStatisticsQuery) =>
    httpClient.get<ArticleProgressStatistics>(`statistics/article/progress`, {
      params,
    }),
  getPaLeadTimeDistribution: (params: PaLeadTimeDistributionQuery) =>
    httpClient.get<PaLeadTimeDistributionStatistics>(
      `statistics/product-approval/lead-time-distribution`,
      { params },
    ),
  getSupplierCoverageHeatmap: (params?: SupplierCoverageHeatmapQuery) =>
    httpClient.get<SupplierCoverageHeatmapStatistics>(
      'statistics/supplier-coverage-heatmap',
      { params },
    ),
  getArticleCoverageHeatmap: (params?: ArticleCoverageHeatmapQuery) =>
    httpClient.get<ArticleCoverageHeatmapStatistics>(
      'statistics/article-coverage-heatmap',
      { params },
    ),
  getPaRejectionReasons: (params: PaRejectionReasonsQuery) =>
    httpClient.get<PaRejectionReasonsStatistics>(
      'statistics/product-approval/rejection-reasons',
      { params },
    ),
};
